import type { CreateUnidadMedidaRequest, UpdateUnidadMedidaRequest } from '../types';

export const UNIDAD_MEDIDA_MAX_LEN = 20;
export const UNIDAD_MEDIDA_DECIMALES_MIN = 0;
export const UNIDAD_MEDIDA_DECIMALES_MAX = 6;

export type UnidadMedidaFormError =
  | 'codigo_obligatorio'
  | 'codigo_max'
  | 'nombre_obligatorio'
  | 'nombre_max'
  | 'decimales_rango';

export const UNIDAD_MEDIDA_ERROR_MSG: Record<UnidadMedidaFormError, string> = {
  codigo_obligatorio: 'El código es obligatorio',
  codigo_max: `El código admite como máximo ${UNIDAD_MEDIDA_MAX_LEN} caracteres`,
  nombre_obligatorio: 'El nombre es obligatorio',
  nombre_max: `El nombre admite como máximo ${UNIDAD_MEDIDA_MAX_LEN} caracteres`,
  decimales_rango: `La cantidad de decimales debe ser un entero entre ${UNIDAD_MEDIDA_DECIMALES_MIN} y ${UNIDAD_MEDIDA_DECIMALES_MAX}`,
};

export function isUnidadMedidaAltaRoute(idUnidadMedida?: string | null): boolean {
  return !idUnidadMedida || idUnidadMedida === 'nuevo';
}

export function normalizeUnidadMedidaCodigo(value: string): string {
  return value.trim().toUpperCase();
}

export function normalizeUnidadMedidaNombre(value: string): string {
  return value.trim();
}

export function normalizeUnidadMedidaDecimales(value: number | string | null | undefined): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return UNIDAD_MEDIDA_DECIMALES_MIN;
  return Math.min(
    UNIDAD_MEDIDA_DECIMALES_MAX,
    Math.max(UNIDAD_MEDIDA_DECIMALES_MIN, Math.trunc(n))
  );
}

export function validateUnidadMedidaForm(input: {
  isNew: boolean;
  codigo: string;
  nombre: string;
  cantDecimales: number | string;
}): UnidadMedidaFormError | null {
  const nombre = normalizeUnidadMedidaNombre(input.nombre);
  if (!nombre) return 'nombre_obligatorio';
  if (nombre.length > UNIDAD_MEDIDA_MAX_LEN) return 'nombre_max';

  if (input.isNew) {
    const codigo = normalizeUnidadMedidaCodigo(input.codigo);
    if (!codigo) return 'codigo_obligatorio';
    if (codigo.length > UNIDAD_MEDIDA_MAX_LEN) return 'codigo_max';
  }

  const raw = typeof input.cantDecimales === 'number' ? input.cantDecimales : Number(input.cantDecimales);
  if (
    !Number.isFinite(raw) ||
    !Number.isInteger(raw) ||
    raw < UNIDAD_MEDIDA_DECIMALES_MIN ||
    raw > UNIDAD_MEDIDA_DECIMALES_MAX
  ) {
    return 'decimales_rango';
  }

  return null;
}

export function buildCreateUnidadMedidaRequest(
  codigo: string,
  nombre: string,
  cantDecimales: number | string = 0
): CreateUnidadMedidaRequest {
  return {
    idUnidadMedida: normalizeUnidadMedidaCodigo(codigo),
    descripcion: normalizeUnidadMedidaNombre(nombre),
    cantDecimales: normalizeUnidadMedidaDecimales(cantDecimales),
    usaBalanza: 'NO',
    porcToleranciaPick: null,
  };
}

export function buildUpdateUnidadMedidaRequest(
  codigo: string,
  nombre: string,
  cantDecimales: number | string = 0
): UpdateUnidadMedidaRequest {
  return {
    idUnidadMedida: normalizeUnidadMedidaCodigo(codigo),
    descripcion: normalizeUnidadMedidaNombre(nombre),
    cantDecimales: normalizeUnidadMedidaDecimales(cantDecimales),
  };
}

export function labelUnidadMedida(u: {
  idUnidadMedida?: string | null;
  descripcion?: string | null;
}): string {
  const id = String(u.idUnidadMedida ?? '').trim();
  const nombre = String(u.descripcion ?? '').trim();
  if (id && nombre) return `${id} — ${nombre}`;
  return id || nombre;
}

export function toUnidadMedidaSelectOptions(
  items: Array<{ idUnidadMedida: string; descripcion?: string | null }> | null | undefined
): Array<{ value: string; label: string }> {
  return (items ?? []).map((u) => ({
    value: u.idUnidadMedida,
    label: labelUnidadMedida(u),
  }));
}
